// ============================================================
// TMR Portal Admin — criar um modelo global ou a exceção de uma clínica
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, Segmented } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { RecordPicker } from '../../components/RecordPicker';
import { SelectField, TextField } from '../../components/fields';
import { Icons } from '../../lib/icons';
import { maskCNPJ } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useCompanySearch } from '../../data/companies/queries';
import type { CompanyListItem } from '../../data/companies/contract';
import { useCreateContractTemplate, useDocumentTypes } from '../../data/contractTemplates/queries';
import {
  findActiveTemplate,
  hasPublishedGlobal,
  legacySlotHolder,
  overrideIneligibility,
} from '../../data/contractTemplates/status';
import type { ContractTemplateSummary, LegacyImportReport } from '../../data/contractTemplates/contract';

/** Props for {@link NewTemplateModal}. */
interface NewTemplateModalProps {
  /**
   * Every template the list holds. Read to find the one a new template would collide with, so the modal can
   * offer to open it instead of letting the backend refuse the creation.
   */
  templates: ContractTemplateSummary[];
  /**
   * What the v1.1 holds that is not here yet. A slot it already takes cannot get a template here: the copy
   * would be refused over there forever (`24` P2b).
   */
  legacyImport?: LegacyImportReport;
  /**
   * The document type to start from — set when the modal is opened from the warning about a type with no
   * global template, which is a request to create exactly that one.
   */
  initialDocumentType?: string;
  /** Closes the modal, carrying the id of the template to open next — a new one, or the existing one. */
  onClose: (templateIdToOpen?: string) => void;
}

/**
 * Creating a template.
 *
 * **Nothing is published here, and that is what makes it safe.** The template comes into being with a
 * draft: an override created by mistake changes nothing, and the clinic keeps signing patients on the
 * global text until somebody publishes (`24` §5). A global template starts empty; an override starts as a
 * copy of the global text in force, which is why one cannot be created for a type whose global was never
 * published (`24` §3.2).
 *
 * The scope choice is the whole decision. A global template is the text every clinic's patients sign; an
 * override is one clinic departing from it — and it may only be created for an Empresa already exported to
 * the legacy, because `produtores.id` is what the issuing engine keys it by. The Empresa is found by a
 * search on the server: the carteira has around a thousand of them, and a list filtered in the browser
 * would only ever offer the first page.
 */
export function NewTemplateModal({
  templates,
  legacyImport,
  initialDocumentType,
  onClose,
}: NewTemplateModalProps) {
  const [documentType, setDocumentType] = useState(initialDocumentType ?? '');
  const [scope, setScope] = useState<'global' | 'company'>('global');
  const [company, setCompany] = useState<CompanyListItem | null>(null);
  const [picking, setPicking] = useState(false);
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [touched, setTouched] = useState(false);

  const canReadCompanies = usePermission(PERMISSIONS.companyRead);
  const types = useDocumentTypes();
  const create = useCreateContractTemplate();

  const typeName = types.data?.find((type) => type.code === documentType)?.name ?? '';
  // Suggested until the operator writes their own — "Tipo — Clínica" for an override, the way the v1.1
  // named them (`24` §3.2). It follows the type and the Empresa while nobody has typed, and is never
  // written over a name somebody did type.
  const suggestedName = scope === 'company' && company && typeName ? `${typeName} — ${company.name}` : typeName;
  const effectiveName = nameEdited ? name : suggestedName;

  const companyId = scope === 'company' ? company?.id ?? null : null;
  const ineligible = scope === 'company' && company ? overrideIneligibility(company) : null;
  const noBaseline = scope === 'company' && Boolean(documentType) && !hasPublishedGlobal(templates, documentType);
  const existing =
    documentType && (scope === 'global' || company)
      ? findActiveTemplate(templates, documentType, companyId)
      : null;

  const legacyHolder =
    documentType && (scope === 'global' || company)
      ? legacySlotHolder(legacyImport, documentType, scope === 'company' ? company?.legacyProducerId ?? null : null)
      : null;

  const missingType = touched && !documentType;
  const missingCompany = touched && scope === 'company' && !company;
  const missingName = touched && !effectiveName.trim();
  const blocked = Boolean(ineligible || existing || noBaseline || legacyHolder);

  async function submit() {
    setTouched(true);
    if (!documentType || !effectiveName.trim() || (scope === 'company' && !company) || blocked) return;

    try {
      const written = await create.mutateAsync({
        documentType,
        name: effectiveName.trim(),
        companyId,
      });

      onClose(written.templateId);
    } catch {
      // Shown below by `create.error`. Swallowed here so a refused creation leaves the form open with
      // what the operator typed still in it.
    }
  }

  return (
    <ModalShell
      title="Novo modelo de contrato"
      lead="Nada do que for criado aqui é emitido antes de ser publicado."
      onClose={() => onClose()}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SelectField
          label="Tipo de documento"
          required
          value={documentType}
          onChange={setDocumentType}
          error={missingType && 'Escolha o tipo de documento.'}
          placeholder={types.isPending ? 'Carregando…' : 'Selecione…'}
          options={(types.data ?? []).map((type) => ({ value: type.code, label: type.name }))}
        />

        <div>
          <span className="pa-form-label">Alcance</span>
          <Segmented<'global' | 'company'>
            value={scope}
            onChange={setScope}
            options={[
              { value: 'global', label: 'Todas as clínicas (modelo global)' },
              { value: 'company', label: 'Exceção de uma clínica' },
            ]}
          />
        </div>

        {scope === 'company' && (
          <CompanyChoice
            company={company}
            canSearch={canReadCompanies}
            missing={missingCompany}
            onPick={() => setPicking(true)}
          />
        )}

        {noBaseline && (
          <Notice tone="warning">
            Publique o modelo global de <strong>{typeName}</strong> antes de criar uma exceção: ela nasce
            como cópia dele.
          </Notice>
        )}

        {ineligible && <Notice tone="warning">{ineligible}</Notice>}

        {legacyHolder && (
          <Notice tone="warning">
            O v1.1 já tem um modelo ativo para este tipo e alcance — <strong>{legacyHolder.name}</strong> — que
            ainda não foi trazido para cá. Importe os modelos do v1.1 em vez de criar outro: os dois não podem
            valer ao mesmo tempo.
          </Notice>
        )}

        {existing && (
          <Notice tone="warning">
            {scope === 'global'
              ? <>Já existe um modelo global de <strong>{typeName}</strong>. </>
              : <>Esta empresa já tem uma exceção de <strong>{typeName}</strong>. </>}
            Um texto novo para ele é uma versão nova desse modelo, não outro modelo.{' '}
            <button
              type="button"
              className="pa-link-btn"
              style={{ font: 'inherit' }}
              onClick={() => onClose(existing.id)}
            >
              Abrir o modelo existente
            </button>
          </Notice>
        )}

        <TextField
          label="Nome do modelo"
          required
          value={effectiveName}
          onChange={(value) => {
            setName(value);
            setNameEdited(true);
          }}
          maxLength={120}
          placeholder="Como este modelo aparece na lista"
          error={missingName && 'Dê um nome ao modelo.'}
        />

        <Notice tone="neutral">
          {scope === 'company' ? (
            <>
              A exceção nasce como <strong>cópia da versão vigente do modelo global</strong>, em rascunho.
              Até ser publicada, a clínica continua assinando pelo texto global.
            </>
          ) : (
            <>
              O modelo nasce com um <strong>rascunho vazio</strong> e não muda nada até ser publicado.
            </>
          )}
        </Notice>

        {create.error && (
          <Notice tone="danger">
            {create.error instanceof ApiError
              ? create.error.detail
              : 'Não foi possível criar o modelo.'}
          </Notice>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <Btn variant="secondary" onClick={() => onClose()}>
            Cancelar
          </Btn>
          <Btn variant="primary" onClick={() => void submit()} disabled={create.isPending || blocked}>
            {create.isPending ? 'Criando…' : 'Criar modelo'}
          </Btn>
        </div>
      </div>

      {picking && (
        <RecordPicker<CompanyListItem>
          eyebrow="Exceção de clínica"
          title="Para qual empresa?"
          subtitle="Os pacientes desta clínica passam a assinar o texto da exceção depois que ela for publicada."
          placeholder="Buscar por nome, CNPJ ou domínio…"
          useSearch={useCompanySearch}
          getKey={(item) => item.id}
          toRow={(item) => ({
            primary: item.name,
            secondary: [
              item.cnpj ? maskCNPJ(item.cnpj) : 'Sem CNPJ',
              item.legacyProducerId === null ? 'ainda não exportada ao legado' : `produtor ${item.legacyProducerId}`,
            ].join(' · '),
          })}
          rowIcon={Icons.clinic}
          idleMessage="Busque a empresa por nome, CNPJ ou domínio."
          notFoundMessage="Nenhuma empresa encontrada."
          onSelect={setCompany}
          onClose={() => setPicking(false)}
        />
      )}
    </ModalShell>
  );
}

/** The chosen Empresa of an override, or the button that opens the search. */
function CompanyChoice({
  company,
  canSearch,
  missing,
  onPick,
}: {
  company: CompanyListItem | null;
  canSearch: boolean;
  missing: boolean;
  onPick: () => void;
}) {
  // The directory is gated by `company.read` on the server. Offering a search that answers 403 on every
  // keystroke is worse than saying why it is not there.
  if (!canSearch) {
    return (
      <Notice tone="warning">
        Escolher a empresa de uma exceção exige a permissão de ver Empresas. Peça o acesso a quem administra
        os papéis.
      </Notice>
    );
  }

  return (
    <div>
      <span className="pa-form-label">
        Empresa <span style={{ color: 'var(--danger)' }}>*</span>
      </span>
      {company ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '9px 12px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>{company.name}</span>
            <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
              {company.cnpj ? maskCNPJ(company.cnpj) : 'Sem CNPJ'}
            </span>
          </div>
          <Btn variant="secondary" size="sm" onClick={onPick}>
            Trocar
          </Btn>
        </div>
      ) : (
        <Btn variant="secondary" icon={Icons.clinic} onClick={onPick}>
          Escolher empresa
        </Btn>
      )}
      {missing && (
        <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--danger)' }}>
          Escolha a empresa desta exceção.
        </div>
      )}
    </div>
  );
}
